import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { uniqBy } from 'lodash';
import useShouldFetchMoreDefault, { IUseShouldFetchMoreConfig } from './useShouldFetchMore';

// import defer from 'shared/lib/defer';

export const STARTING_PAGE = 1;
const PAGE_SIZE = 20;

export interface IInfiniteScrollApolloParams {
  query: any;
  variables: Record<string, any>;
  itemIdKey?: string | Function;
  dataResultsKey: string;
  targetScrollOffset?: number;
  scrollDelay?: number;
  trackVariablesForQueryReset?: any[];
  maxAmountOfPages?: number;
  getFetchMoreVariables?: () => any;
}

/**
 * `useQuery` with built-in support for infinite load (via fetchMore).
 * @param {object} options
 * @param {string} options.query The gql query passed to `useQuery`.
 * @param {string | string[]} options.dataResultsKey The key(s) where your data
 *  lives in the query result.
 * @param {object} [options.variables={}] Variables your query uses. Note that
 *  `pageNumber` and `pageSize` are handled internally.
 *  NOTE: Variables defined here should also be passed to `options.trackVariablesForQueryReset`
 *  if they should reset the query and fetch page 1 results again.
 * @param {array} options.trackVariablesForQueryReset Pass your variables (if any) here.
 *  When a variable changes, the query is reset to page 1 so new results are fetched.
 * @param {string} [options.itemIdKey="id"] The id key for items, used to deduplicate results.
 * @param {string} [options.scrollDelay=300] Throttle scroll events.
 * @param {string} [options.targetScrollOffset=600] Offset to trigger a fetchMore.
 * @returns {object} Returns a subset of `useQuery` with the addition of `loadingMore`.
 */
const useInfiniteScrollApolloQuery = ({
  query,
  dataResultsKey,
  variables = {},
  itemIdKey = 'id',
  maxAmountOfPages,
  trackVariablesForQueryReset = [],
  scrollDelay = 300,
  targetScrollOffset = 600,
  getFetchMoreVariables
}: IInfiniteScrollApolloParams, {
  // Used for mocking in tests.
  useShouldFetchMore = useShouldFetchMoreDefault,
  pageSize = PAGE_SIZE,
  scrollRef
}: {
  useShouldFetchMore?: (config: IUseShouldFetchMoreConfig) => boolean;
  pageSize?: number;
  scrollRef?: React.RefObject<HTMLElement>;
} = {}) => {
  let dataResultsKeys: string[];
  if (Array.isArray(dataResultsKey)) {
    dataResultsKeys = dataResultsKey;
  } else {
    dataResultsKeys = [dataResultsKey];
  }
  const isInitialMount = useRef(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resultsExhausted, setResultsExhausted] = useState(false);
  const shouldFetchMore = useShouldFetchMore({
    scrollDelay,
    targetScrollOffset,
    scrollRef
  });

  const {
    data, error, loading, refetch, fetchMore
  } = useQuery(query, {
    variables: {
      ...variables,
      // `pageNumber` here should always be the first page
      // since `fetchMore` handles follow-up pages.
      pageNumber: STARTING_PAGE,
      pageSize,
    },
  });

  // If there's Apollo cache, then use pageNumber from cache
  // to resume loading from where we left off.
  const [page, setPage] = useState(data?.[dataResultsKeys[0]]?.pageNumber || STARTING_PAGE);

  const reset = async () => {
    setResetting(true);
    setPage(STARTING_PAGE);
    setResultsExhausted(false);
    await refetch();
    setResetting(false);
  };

  // Track changes to certain variables to page reset.
  useEffect(() => {
    // This useEffect only cares about updates, not initial mount.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (page !== STARTING_PAGE || resultsExhausted) {
      setPage(STARTING_PAGE);
      setResultsExhausted(false);
    }
  }, trackVariablesForQueryReset);
  const limitRequest = !!maxAmountOfPages && maxAmountOfPages < page + 1;
  // Trigger fetchMore when they hit the scroll point.
  if (shouldFetchMore &&
    !limitRequest &&
    !loading &&
    !isRefetching &&
    !resultsExhausted &&
    !error) {
    setIsRefetching(true);
  }

  useEffect(() => {
    if (!isRefetching || resultsExhausted || error) {
      return;
    }

    const nextPage = page + 1;

    const fetchMoreVariables = getFetchMoreVariables ? getFetchMoreVariables() : {};

    fetchMore({
      variables: {
        ...fetchMoreVariables,
        pageNumber: nextPage,
      },
      updateQuery: (prev: any, { fetchMoreResult }: any) => {
        const updatedResults: any = {};
        // Return previous result if exhausted.
        const isExhausted = dataResultsKeys.every((_dataResultsKey) => {
          if (
            !fetchMoreResult
            || fetchMoreResult[_dataResultsKey].length === 0
            || (
              fetchMoreResult[_dataResultsKey].items
              && fetchMoreResult[_dataResultsKey].items.length === 0
            )
          ) {
            return true;
          }
          return false;
        });

        if (isExhausted) {
          setResultsExhausted(true);
          setIsRefetching(false);
          return prev;
        }

        dataResultsKeys.forEach((_dataResultsKey) => {
          let prevResults = data[_dataResultsKey];
          if (prev && prev[_dataResultsKey]) {
            prevResults = prev[_dataResultsKey];
          }

          const newResults = fetchMoreResult[_dataResultsKey];

          let combinedItems;

          // Combine all items and remove duplicates.
          if (prevResults.items) {
            combinedItems = uniqBy(prevResults.items.concat(newResults.items), itemIdKey);
          } else {
            combinedItems = uniqBy(prevResults.concat(newResults), itemIdKey);
          }

          // Merge the results. fetchMoreResult takes precedence.
          updatedResults[_dataResultsKey] = {
            ...prevResults,
            ...newResults,
            pageNumber: newResults.pageNumber,
            items: combinedItems,
            itemCount: combinedItems.length,
          };

          if (data[_dataResultsKey] && !data[_dataResultsKey].items) {
            data[_dataResultsKey] = combinedItems;
          }
        });

        /**
         * We need to defer this setter (setTimeout of 0) to prevent a race condition where
         * a second request is fired before the first one has rendered and pushed the scroll
         * target down lower.
         */
        setTimeout(() => {
          setPage(nextPage);
          setIsRefetching(false);
        }, 1);

        return updatedResults;
      },
    });
  }, [isRefetching]);

  return {
    data,
    error,
    loading,
    loadingMore: isRefetching,
    refetch,
    reset,
    resetting,
  };
};

export default useInfiniteScrollApolloQuery;
